<template>
  <!-- Modal de carga/corrección de TC. Vive en core/components porque lo usan
       el mantenedor y la barra de aviso del dashboard: si estuviera en una de
       las dos pantallas, la otra lo copiaría. -->
  <!-- El clic en el fondo NO cierra. El operador copia el TC de la web de
       SUNAT, vuelve al portal, hace un clic en cualquier lado y perdía lo
       escrito. Se cierra solo con Cancelar o al guardar bien. -->
  <div v-if="value" class="tc-overlay">
    <div class="tc-modal">
      <div class="tc-header">
        <h3>{{ editando ? 'Corregir tipo de cambio' : 'Cargar tipo de cambio' }}</h3>
        <!-- Hace lo mismo que Cancelar: cierra sin guardar y sin preguntar.
             Se deshabilita mientras guarda, como los otros dos botones. -->
        <button type="button" class="tc-close" aria-label="Cerrar"
          :disabled="guardando" @click="cerrar">
          <i class="ri-close-line"></i>
        </button>
      </div>

      <label class="tc-field">
        <span>Fecha</span>
        <!-- Al corregir, la fecha no se mueve: reasignarla reescribiría los
             montos ya mostrados de otro día. -->
        <input v-model="form.fecha" type="date" :max="hoy" :disabled="editando" />
      </label>
      <label class="tc-field">
        <span>Compra</span>
        <input v-model.number="form.compra" type="number" step="0.0001" min="0" placeholder="3.7520" />
      </label>
      <label class="tc-field">
        <span>Venta</span>
        <input v-model.number="form.venta" type="number" step="0.0001" min="0" placeholder="3.7600" />
      </label>

      <p v-if="error" class="tc-error">{{ error }}</p>
      <p class="tc-hint">Se valoriza con el tipo de compra.</p>

      <div class="tc-actions">
        <button class="tc-btn" :disabled="guardando" @click="cerrar">Cancelar</button>
        <button class="tc-btn tc-btn--primary" :disabled="guardando" @click="guardar">
          {{ guardando ? 'Guardando…' : 'Guardar' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ExchangeRateService } from "@/core/services";
import { hoyLocal } from "@/core/utils/fecha";

export default {
  name: "ExchangeRateModal",
  props: {
    value: { type: Boolean, default: false },
    // Cuando viene un TC, el modal corrige en vez de crear.
    rate: { type: Object, default: null },
  },
  data() {
    return {
      form: { fecha: "", compra: null, venta: null },
      guardando: false,
      error: "",
    };
  },
  computed: {
    editando() {
      return !!(this.rate && this.rate.id);
    },
    hoy() {
      return hoyLocal();
    },
  },
  watch: {
    value(abierto) {
      if (!abierto) return;
      this.error = "";
      this.form = this.rate
        ? { fecha: this.rate.fecha, compra: Number(this.rate.compra), venta: Number(this.rate.venta) }
        : { fecha: this.hoy, compra: null, venta: null };
    },
  },
  methods: {
    cerrar() {
      this.$emit("input", false);
    },
    async guardar() {
      this.error = "";
      if (!this.form.fecha) { this.error = "La fecha es obligatoria."; return; }
      if (!this.form.compra || !this.form.venta) { this.error = "Compra y venta son obligatorias."; return; }

      this.guardando = true;
      try {
        if (this.editando) {
          await ExchangeRateService.update(this.rate.id, this.form);
        } else {
          await ExchangeRateService.save(this.form);
        }
        this.$emit("input", false);
        // El padre decide qué recargar: el mantenedor su lista, el dashboard
        // sus KPIs, y ninguno de los dos recarga la página.
        this.$emit("saved");
      } catch (e) {
        this.error = (e && e.errors && e.errors.message) || "No se pudo guardar el tipo de cambio.";
      } finally {
        this.guardando = false;
      }
    },
  },
};
</script>

<style lang="stylus" scoped>
.tc-overlay
  position fixed
  inset 0
  background rgba(17, 24, 39, 0.45)
  display flex
  align-items center
  justify-content center
  z-index 1050

.tc-modal
  background #fff
  border-radius 10px
  padding 20px 22px
  width 340px
  max-width calc(100vw - 32px)
  h3
    margin 0
    font-size 1rem
    font-weight 600

.tc-header
  display flex
  align-items flex-start
  justify-content space-between
  gap 12px
  margin-bottom 14px

.tc-close
  border none
  background transparent
  padding 0
  line-height 1
  font-size 20px
  color #9F9F9F
  cursor pointer
  &:hover:not(:disabled)
    color #302F2F
  &:disabled
    opacity .5
    cursor default

.tc-field
  display block
  margin-bottom 10px
  span
    display block
    font-size 0.76rem
    color #6b7280
    margin-bottom 3px
  input
    width 100%
    border 1px solid #d1d5db
    border-radius 6px
    padding 6px 8px
    font-size 0.88rem
    &:disabled
      background #f3f4f6

.tc-error
  color #EF4444
  font-size 0.78rem
  margin 4px 0 0

.tc-hint
  color #9ca3af
  font-size 0.72rem
  margin 8px 0 0

.tc-actions
  display flex
  justify-content flex-end
  gap 8px
  margin-top 16px

.tc-btn
  border 1px solid #d1d5db
  background #fff
  border-radius 6px
  padding 6px 14px
  font-size 0.84rem
  cursor pointer
  &--primary
    background #365EFF
    border-color #365EFF
    color #fff
  &:disabled
    opacity 0.6
    cursor default
</style>
